import { forwardRef, useMemo } from 'react';
import styled from 'styled-components';

import { COLORS } from '@/shared/constants/colors';
import { BeneficiaryGroup } from '@/shared/lib/sj-orm';
import { BaseProps } from '@/shared/types';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';

import { BeneficiaryGroupI18n as BeneficiaryGroupI18n } from './BeneficiaryGroupI18n';

type SelectBeneficiaryGroupProps = BaseProps<{
  filter?: (type: BeneficiaryGroup) => boolean;
  onSelect: (type: BeneficiaryGroup) => void;
}>;

const list = [BeneficiaryGroup.FAMILY, BeneficiaryGroup.FAMILY_CLAN, BeneficiaryGroup.CIRCLE];

export const SelectBeneficiaryGroup = forwardRef<HTMLDivElement, SelectBeneficiaryGroupProps>(
  ({ onSelect, filter, ...props }, ref) => {
    const items = useMemo(() => (filter ? list.filter(filter) : list), [filter]);
    const { t } = useTranslation('entities/beneficiaries');

    return (
      <Container {...props} className="uikitScrollbarNone" ref={ref}>
        <Title>{t('selectBeneficiaryGroupTitle')}</Title>
        <Group>
          {items.map((group, index) => (
            <Item onClick={() => onSelect(group)} key={index}>
              <BeneficiaryGroupI18n group={group} />
            </Item>
          ))}
        </Group>
      </Container>
    );
  },
);

SelectBeneficiaryGroup.displayName = 'SelectBeneficiaryGroup';

const Container = styled.div`
  width: 320px;
  padding: 4px;
  border-radius: 16px;
  border: 1px solid ${COLORS.systemDark100};
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: ${COLORS.systemLight300};
`;

const Group = styled.div`
  padding: 4px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: ${COLORS.systemLight100};
`;

const Item = styled.div`
  height: 44px;
  padding: 8px 12px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  &:hover {
    background: ${COLORS.systemLight200};
  }
`;

const Title = styled.div`
  font-size: 12px;
  color: ${COLORS.systemDark200};
  padding: 10px 0 0 12px;
`;
